import { useCallback, useEffect, useReducer, useRef, useState } from "react"
import { useSelector } from "react-redux"
import DataExportReducer, { initialDataExportsData } from "./reducers/data_export_reducer"
import { get_data_exports } from "./api"
import { setState } from "../../utils/CommonReducer"
import Pagination from "../../../Pagination"
import { Popup } from "../../../design_system/components/Popup/Popup.jsx"
import { Loader } from "../../../design_system/components/Loader.jsx"
import { Badge } from "../../../design_system/components/Input Fields/Badge.jsx"
import { Icons } from "../../../design_system/components/Icons"
import { Button } from "../../../design_system/components/Button/Button.jsx"
import { Tooltip } from "../../../design_system/components/ToolTip/ToolTip.jsx"
import { useToast } from "../../../design_system/components/Toast/ToastContext.jsx"
import ExportAppliedTags from "./export_applied_tags"
import ConditionalRender from "../../../ConditionalRender"
import { canUseExportSource } from "../../../utils/access"

const STATUS_BADGE_TYPE = {
    'inprogress': 'Pending',
    'delayed': 'Warning',
    'failed': 'Error',
    'completed': 'Success',
}
const STATUS_LABEL = {
    'inprogress': 'Processing',
    'delayed': 'Delayed',
    'failed': 'Failed',
    'completed': 'Completed',
}
const STATUS_REASON = {
    'delayed': 'Reason: Processing delay due to high data volume. Please wait.',
    'failed': "This issue has been reported to our team. We'll get back to you soon.",
}
export const DATA_EXPORT_NAME = {
    'NewsFeed': 'Newsfeed',
    'MatchingArticle': 'Newsfeed',
    'LinkedinProspect': 'Directory',
    'NewLinkedinProspect': 'Directory',
}

const TREE_EXPORT_TYPES = ['NewLinkedinProspect', 'CustomReport']
const isTreeExportType = (type) => TREE_EXPORT_TYPES.includes(type)

const hasAppliedFilters = (data_export) => {
    if (data_export.type === 'AiReport') return false;

    if (isTreeExportType(data_export.type)) {
        if (!data_export.qb_tree) return false;
        try {
            const tree = typeof data_export.qb_tree === 'string'
                ? JSON.parse(data_export.qb_tree)
                : data_export.qb_tree;
            
            const countRules = (node) => {
                if (!node) return 0;
                let count = 0;
                const children = Array.isArray(node.children1)
                    ? node.children1
                    : Object.values(node.children1 || {});
                children.forEach((child) => {
                    if (child.type === "rule") {
                        count += 1;
                    } else {
                        count += countRules(child);
                    }
                });
                return count;
            };
            return countRules(tree) > 0;
        } catch (e) {
            return false;
        }
    } else {
        return !!(data_export.tags && data_export.tags.length > 0);
    }
};

export const getExportDisplayName = (data_export) => {
    const mapped = DATA_EXPORT_NAME[data_export.type];
    if (mapped) return mapped;
    if (data_export.type === 'CustomReport' || data_export.type === 'AiReport') {
        const reportType = data_export.type.replace('Report', '');
        return `${data_export.name} (${reportType} report)`;
    }
    if (!data_export.type) return 'Prospects';
    return data_export.name || data_export.type;
}
export const DataExports = ({ onClose }) => {
    const currentTeam = useSelector((state) => state.current_team.team)
    const hasInternalCrm = currentTeam?.internal_crm_enabled === true
    const availableSources = [
        ...(canUseExportSource({ team: currentTeam, source: 'crm', internalCrmEnabled: hasInternalCrm }) && hasInternalCrm ? ['crm'] : []),
        ...(canUseExportSource({ team: currentTeam, source: 'prospects_feed', internalCrmEnabled: hasInternalCrm }) ? ['prospects_feed'] : []),
    ]
    const defaultSource = availableSources.includes('prospects_feed') ? 'prospects_feed' : availableSources[0]
    const { error: showErrorToast } = useToast()
    const [pageData, pageDataReducer] = useReducer(DataExportReducer, initialDataExportsData)
    const [activeSource, setActiveSource] = useState(defaultSource)
    const latestRequest = useRef(0)
    const { totalPages, currentPage, data, isFetching } = pageData
    const fetchData = useCallback((page, source) => {
        const requestId = latestRequest.current + 1
        latestRequest.current = requestId
        setState(pageDataReducer)({
            data: [],
            totalPages: 0,
            isFetching: { loadingPageData: true },
        })
        const requestParams = hasInternalCrm ? { page, source } : { page }
        get_data_exports(requestParams).then((res) => {
            if (latestRequest.current !== requestId) return
            setState(pageDataReducer)({
                data: res.data.data,
                totalPages: res.data.total_pages,
                totalCount: res.data.total,
                currentPage: page,
                isFetching: { loadingPageData: false },
            })
        }).catch((error) => {
            if (latestRequest.current === requestId) {
                const isDatamineExportsUnavailable = hasInternalCrm
                    && source === 'crm'
                    && error.response?.data?.error_code === 'datamine_exports_unavailable'
                if (isDatamineExportsUnavailable) {
                    showErrorToast(error.response?.data?.error || 'DataMine exports are temporarily unavailable.')
                }
                setState(pageDataReducer)({ isFetching: { loadingPageData: false } })
            }
        })
    }, [hasInternalCrm, showErrorToast])
    useEffect(() => {
        if (!activeSource || !availableSources.includes(activeSource)) {
            if (defaultSource) setActiveSource(defaultSource)
            return
        }
        fetchData(1, activeSource)
    }, [activeSource, defaultSource, fetchData]) // eslint-disable-line react-hooks/exhaustive-deps
    const handleOnClosePopup = () => setState(pageDataReducer)({ popupList: {}, currentExport: {} })

    const renderTableContent = () => (
        <div className="data-exports__table-wrap">
            <table className="data-fields__table data-exports__table">
                <thead>
                    <tr>
                        <th className="body-m-bold">Export Name</th>
                        <th className="body-m-bold">Date & Time</th>
                        <th className="body-m-bold">
                            <div className="flex items-center cursor-pointer" style={{ gap: 'var(--space-xs)' }}>
                                Status
                                <Tooltip
                                    tooltipText="Report generation might take anywhere between 2 min - 45 min depending on the data volume. You can download here when report is ready."
                                    showWithoutOverflowing
                                    placement="top"
                                >
                                    <Icons name="info" size={16} />
                                </Tooltip>
                            </div>
                        </th>
                        <th className="body-m-bold">Progress</th>
                        <th className="body-m-bold">Action</th>
                    </tr>
                </thead>
                <tbody>
                    {data?.map(data_export => (
                        <tr key={data_export.id}>
                            <td>
                                <div>
                                    <div className="body-m-semibold">{getExportDisplayName(data_export)}</div>
                                    <ConditionalRender if={hasAppliedFilters(data_export)}>
                                        <div
                                            className="data-exports__filters-badge"
                                            onClick={() => setState(pageDataReducer)({ popupList: { exportTagsPopup: true }, currentExport: data_export })}
                                        >
                                            <Icons name="filter-sliders" size={16} />
                                            <span className="body-s-bold">
                                                {isTreeExportType(data_export.type)
                                                    ? 'View applied filters'
                                                    : `${data_export.tags.length} ${data_export.tags.length === 1 ? 'filter' : 'filters'} applied`
                                                }
                                            </span>
                                        </div>
                                    </ConditionalRender>
                                </div>
                            </td>
                            <td className="body-m-semibold">{data_export.created_time}</td>
                            <td>
                                <Badge type={STATUS_BADGE_TYPE[data_export.state] || 'Neutral'} label={STATUS_LABEL[data_export.state] || data_export.state} />
                            </td>
                            <td className="body-m-semibold">{data_export.progress}%</td>
                            <td>
                                {data_export.download_url ? (
                                    <Button
                                        Type="Tertiary"
                                        Size="Small"
                                        ShowLeadingIcon={true}
                                        LeadingIcon="download"
                                        onClick={() => window.open(data_export.download_url, '_blank')}
                                    >
                                        Download
                                    </Button>
                                ) : (
                                    <span className="body-m-semibold color-text-primary">{STATUS_REASON[data_export.state] || ''}</span>
                                )}
                            </td>
                        </tr>
                    ))}
                    <ConditionalRender if={data?.length === 0}>
                        <tr>
                            <td colSpan="5">
                                <div className="data-exports__empty">
                                    <div className="body-m-semibold color-text-secondary">No exports have been requested yet.</div>
                                </div>
                            </td>
                        </tr>
                    </ConditionalRender>
                </tbody>
            </table>
        </div>
    )

    return (
        <Popup name="Data Exports" handleOnClosePopup={onClose} maxWidth="78vw" showHeaderDivider={false} customContentStyle={{ padding: 0 }}>
            <div className="data-exports">
                <div className="data-exports__header">
                    <div className="body-s color-text-secondary">
                        Export up to 5 files at a time. Please wait for an ongoing export to finish before starting a new one
                    </div>
                </div>
                {hasInternalCrm && availableSources.length > 1 && (
                    <div className="data-exports__source-tabs" role="tablist" aria-label="Export source">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeSource === 'crm'}
                            className={activeSource === 'crm' ? 'active' : ''}
                            onClick={() => setActiveSource('crm')}
                        >
                            DataMine
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeSource === 'prospects_feed'}
                            className={activeSource === 'prospects_feed' ? 'active' : ''}
                            onClick={() => setActiveSource('prospects_feed')}
                        >
                            Prospects &amp; Feed
                        </button>
                    </div>
                )}
                <Pagination
                    totalPages={totalPages}
                    currentPage={currentPage}
                    onPageChange={(page) => fetchData(page, activeSource)}
                />
                <div className="mt-m">
                    {!isFetching.loadingPageData
                        ? renderTableContent()
                        : <div className="data-exports__loader"><Loader isLoading={true} Size="Large" isFullPage={false} /></div>
                    }
                </div>
            </div>
            {pageData.popupList.exportTagsPopup && (
                <ExportAppliedTags data_export={pageData.currentExport} handleOnClosePopup={handleOnClosePopup} />
            )}
        </Popup>
    )
}
export default DataExports
